import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';

// App-wide notifications: `const toast = useToast(); toast.error('Could not …')`.
// Replaces window.alert(), which blocks the page and cannot be styled.

const ToastContext = createContext(null);

const ICONS = {
  success: { Icon: CheckCircle2, cls: 'text-pp-get' },
  error: { Icon: AlertCircle, cls: 'text-pp-delete' },
  info: { Icon: Info, cls: 'text-pp-accent' },
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((type, message, duration) => {
    const id = nextId.current++;
    // Errors stay longer: they usually need reading twice.
    const ms = duration ?? (type === 'error' ? 7000 : 3500);
    setToasts((prev) => [...prev.slice(-3), { id, type, message }]);
    setTimeout(() => dismiss(id), ms);
    return id;
  }, [dismiss]);

  const api = useRef(null);
  if (!api.current) {
    api.current = {
      success: (message, duration) => show('success', message, duration),
      error: (message, duration) => show('error', message, duration),
      info: (message, duration) => show('info', message, duration),
    };
  }

  return (
    <ToastContext.Provider value={api.current}>
      {children}
      <div
        className="pointer-events-none fixed bottom-5 right-5 z-[60] flex w-[min(360px,calc(100vw-2.5rem))] flex-col gap-2"
        aria-live="polite"
      >
        {toasts.map((t) => {
          const { Icon, cls } = ICONS[t.type] || ICONS.info;
          return (
            <div key={t.id} role={t.type === 'error' ? 'alert' : 'status'} className="pp-toast pointer-events-auto">
              <Icon size={16} className={`mt-0.5 flex-shrink-0 ${cls}`} />
              <p className="min-w-0 flex-1 leading-snug">{t.message}</p>
              <button
                type="button"
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss"
                className="-mr-1 -mt-0.5 rounded p-1 text-pp-faint transition-colors hover:text-pp-text"
              >
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

const NOOP = { success() {}, error() {}, info() {} };

export function useToast() {
  return useContext(ToastContext) || NOOP;
}
